import { defineNuxtPlugin } from '#app';
import { useMainStore } from '~/stores/mainStore';

const BACKGROUND_MUSIC_KEY = '__background_music__';

let soundInstance: typeof import('@pixi/sound').sound | null = null;

async function getSound() {
  if (!soundInstance) {
    const { sound } = await import('@pixi/sound');
    soundInstance = sound;
  }
  return soundInstance;
}

export default defineNuxtPlugin(async () => {
  if (!import.meta.client) return;

  const sound = await getSound();
  sound.disableAutoPause = true;
  const bgMusic = sound.exists(BACKGROUND_MUSIC_KEY) ? sound.find(BACKGROUND_MUSIC_KEY) : null;
  const store = useMainStore();
  const targetVolumes = {
    music: store.volume.music,
    ambient: store.volume.ambient
  };

  const currentVolumes = {
    music: store.volume.music,
    ambient: store.volume.ambient
  };

  const fadeIntervals: Record<'music' | 'ambient', ReturnType<typeof setInterval> | null> = {
    music: null,
    ambient: null
  };

  const FADE_DURATION = 40; // seconds
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  let isPageActive = true;
  let isTransitioning = false;
  let needsUserGestureResume = false;

  watch(
    () => store.volume.music,
    async (newVolume) => {
      targetVolumes.music = newVolume;
      if (isPageActive) {
        currentVolumes.music = newVolume;
        if (bgMusic) {
          bgMusic.volume = newVolume;
        }
      }
    }
  );

  watch(
    () => store.volume.ambient,
    async (newVolume) => {
      targetVolumes.ambient = newVolume;
      if (isPageActive) {
        currentVolumes.ambient = newVolume;
        const openedBuilding = store.openedBuilding;
        if (openedBuilding) {
          const sound = await getSound();
          const ambientSound = sound.find(openedBuilding);
          if (ambientSound) {
            ambientSound.volume = newVolume;
          }
        }
      }
    }
  );

  const clearFade = (type: 'music' | 'ambient') => {
    if (fadeIntervals[type]) {
      clearInterval(fadeIntervals[type]!);
      fadeIntervals[type] = null;
    }
  };

  const fadeVolume = async (type: 'music' | 'ambient', target: number, durationSeconds: number) => {
    const intervalMs = 50; // Longer interval is fine with absolute time calculation
    clearFade(type);

    const startVolume = currentVolumes[type];
    const distance = Math.abs(target - startVolume);

    // If already at target or no distance, just set and return
    if (distance < 0.0001) {
      currentVolumes[type] = target;
      return;
    }

    const startTime = performance.now();
    const durationMs = durationSeconds * 1000;

    const id = setInterval(async () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / durationMs, 1);

      // Linear interpolation from start to target based on absolute elapsed time
      const current = startVolume + (target - startVolume) * progress;

      if (progress >= 1) {
        currentVolumes[type] = target;
        clearFade(type);
      } else {
        currentVolumes[type] = parseFloat(current.toFixed(4));
      }

      if (type === 'music') {
        if (bgMusic) {
          bgMusic.volume = currentVolumes[type];
        }
      } else {
        const openedBuilding = store.openedBuilding;
        if (openedBuilding) {
          const sound = await getSound();
          const ambientSound = sound.find(openedBuilding);
          if (ambientSound) {
            ambientSound.volume = currentVolumes[type];
          }
        }
      }
    }, intervalMs);

    fadeIntervals[type] = id;
  };

  const pauseAudio = async () => {
    if (bgMusic && bgMusic.isPlaying) {
      bgMusic.pause();
    }

    const openedBuilding = store.openedBuilding;
    if (openedBuilding) {
      const ambientSound = sound.find(openedBuilding);
      if (ambientSound && ambientSound.isPlaying) {
        ambientSound.pause();
      }
    }
  };

  const resumeAudioContext = async () => {
    const sound = await getSound();
    // Resume the underlying AudioContext if suspended (required on mobile after app background)
    const audioContext = sound.context?.audioContext;
    console.log('[SoundFade] resumeAudioContext - state:', audioContext?.state, 'isMobile:', isMobile);
    if (audioContext && audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
        console.log('[SoundFade] AudioContext resumed, new state:', audioContext.state);
      } catch (e) {
        console.warn('[SoundFade] Failed to resume AudioContext:', e);
      }
    }
    // Also unpause pixi/sound's global pause state
    if (sound.context) {
      sound.context.paused = false;
    }
  };

  const resumeAudio = async (fromUserGesture = false) => {
    await resumeAudioContext();

    const sound = await getSound();
    const audioContext = sound.context?.audioContext;

    // On mobile, if AudioContext is still suspended after resume attempt, we need user gesture
    if (isMobile && audioContext?.state === 'suspended' && !fromUserGesture) {
      console.log('[SoundFade] AudioContext still suspended, waiting for user gesture');
      needsUserGestureResume = true;
      return;
    }

    if (bgMusic) {
      bgMusic.volume = 0;
      // On mobile, state can be stale after app suspension - force stop then play
      if (isMobile) {
        try {
          bgMusic.stop();
        } catch (_) {
          /* empty */
        }
        try {
          bgMusic.play();
          console.log('[SoundFade] Mobile: force played bgMusic');
          needsUserGestureResume = false;
        } catch (e) {
          console.warn('[SoundFade] Failed to play bgMusic, waiting for user gesture:', e);
          needsUserGestureResume = true;
        }
      } else {
        if (bgMusic.paused) {
          bgMusic.resume();
        } else if (!bgMusic.isPlaying) {
          bgMusic.play();
        }
      }
    }

    const openedBuilding = store.openedBuilding;
    if (openedBuilding) {
      const ambientSound = sound.find(openedBuilding);
      if (ambientSound) {
        ambientSound.volume = 0;
        if (isMobile) {
          try {
            ambientSound.stop();
          } catch (_) {
            /* empty */
          }
          try {
            ambientSound.play();
          } catch (_) {
            /* empty */
          }
        } else {
          if (ambientSound.paused) {
            ambientSound.resume();
          } else if (!ambientSound.isPlaying) {
            ambientSound.play();
          }
        }
      }
    }
  };

  const handleInactive = async (force = false) => {
    if ((!isPageActive || isTransitioning) && !force) return;
    isTransitioning = true;
    isPageActive = false;

    // Clear any ongoing fade-in
    clearFade('music');
    clearFade('ambient');

    targetVolumes.music = store.volume.music;
    targetVolumes.ambient = store.volume.ambient;

    await fadeVolume('music', 0, FADE_DURATION);
    await fadeVolume('ambient', 0, FADE_DURATION);

    isTransitioning = false;

    setTimeout(async () => {
      if (!isPageActive) {
        await pauseAudio();
      }
    }, FADE_DURATION * 1000);
  };

  const handleActive = async (force = false, fromUserGesture = false) => {
    if ((isPageActive || isTransitioning) && !force) return;
    isTransitioning = true;
    isPageActive = true;

    // Clear any ongoing fade-out
    clearFade('music');
    clearFade('ambient');

    currentVolumes.music = 0;
    currentVolumes.ambient = 0;

    await resumeAudio(fromUserGesture);

    // If we're waiting for user gesture, don't proceed with fade
    if (needsUserGestureResume) {
      isTransitioning = false;
      isPageActive = false;
      console.log('[SoundFade] handleActive: waiting for user gesture, not starting fade');
      return;
    }

    isTransitioning = false;

    setTimeout(async () => {
      if (isPageActive && !needsUserGestureResume) {
        await fadeVolume('music', targetVolumes.music, FADE_DURATION);
        await fadeVolume('ambient', targetVolumes.ambient, FADE_DURATION);
      }
    }, 50);
    console.log('[SoundFade] handleActive completed');
  };

  // Visibility change is the most reliable event, especially on mobile
  document.addEventListener('visibilitychange', async () => {
    console.log(
      '[SoundFade] visibilitychange - hidden:',
      document.hidden,
      'isPageActive:',
      isPageActive,
      'isTransitioning:',
      isTransitioning
    );
    // Reset transitioning state on visibility change (can get stuck if app was suspended)
    isTransitioning = false;

    if (document.hidden) {
      // Force state sync based on actual visibility
      isPageActive = true; // Set to true so handleInactive will run
      await handleInactive();
    } else {
      // Force state sync based on actual visibility
      isPageActive = false; // Set to false so handleActive will run
      await handleActive();
    }
  });

  // Additional mobile-specific resume handling
  if (isMobile) {
    window.addEventListener('resume', () => {
      console.log('[SoundFade] resume event fired');
      isTransitioning = false;
      isPageActive = false;
      handleActive();
    });

    // iOS-specific: handle when page becomes interactive again
    const handleUserGestureResume = async () => {
      const sound = await getSound();
      const audioContext = sound.context?.audioContext;

      if (audioContext?.state === 'suspended') {
        console.log('[SoundFade] User gesture - resuming suspended AudioContext');
        try {
          await audioContext.resume();
          console.log('[SoundFade] AudioContext resumed via user gesture, state:', audioContext.state);
        } catch (e) {
          console.warn('[SoundFade] Failed to resume AudioContext via user gesture:', e);
          return;
        }
      }

      // If we were waiting for user gesture to resume audio, do it now
      if (needsUserGestureResume && !document.hidden) {
        console.log('[SoundFade] User gesture detected, resuming audio playback');
        needsUserGestureResume = false;
        isTransitioning = false;
        isPageActive = false;
        await handleActive(true, true);
      }
    };

    document.addEventListener('touchstart', handleUserGestureResume, { passive: true });
    document.addEventListener('click', handleUserGestureResume, { passive: true });
  }

  // Only use blur/focus on desktop - mobile fires these unreliably alongside visibilitychange
  if (!isMobile) {
    window.addEventListener('blur', () => handleInactive());
    window.addEventListener('focus', () => handleActive());
  }

  window.addEventListener('pagehide', () => {
    handleInactive();
  });

  window.addEventListener('pageshow', (event) => {
    // Force resume on pageshow, especially for bfcache restoration
    if (event.persisted) {
      isTransitioning = false;
      isPageActive = false;
      handleActive(true);
    }
  });

  if (isMobile) {
    window.addEventListener('beforeunload', () => {
      pauseAudio();
    });
  }
});
